import { START_DATE_ALIAS_FIELD_ID } from '@atlassian/jira-business-constants/src/index.tsx';
import { transformTopLevelFields } from '@atlassian/jira-business-fields/src/common/transform/index.tsx';
import type { JWMGetItemsCacheVariables } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import type { IntlShape } from '@atlassian/jira-intl';
import {
	ISSUE_KEY_TYPE,
	ASSIGNEE_TYPE,
	DUE_DATE_TYPE,
	UPDATED,
	CREATED,
	RANK_CF_TYPE,
	ISSUE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
	SUMMARY_TYPE,
	LABELS_TYPE,
	TIME_ESTIMATE_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { DateOnly, toDateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import type {
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges as ItemEdge,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields as ResponseJiraField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraStatusField as ResponseJiraStatusField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleLineTextField as ResponseJiraSingleLineTextField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraIssueTypeField as ResponseJiraIssueTypeField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraDatePickerField as ResponseJiraDatePickerField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraDateTimePickerField as ResponseJiraDateTimePickerField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraPeopleField_selectedUsersConnection_edges_node as ResponseAtlassianUserAccount,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectUserPickerField as ResponseJiraSingleSelectUserPickerField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraPriorityField as ResponseJiraPriorityField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraLabelsField as ResponseJiraLabelsField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraOriginalTimeEstimateField as ResponseJiraOriginalTimeEstimateField,
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_childSummary as ResponseJiraChildSummaryField,
} from './__generated_apollo__/JWMListViewItems';
import { listViewTransformFields } from './field-transformer/index.tsx';
import type { ListViewIssue } from './types.tsx';

export type BaseFieldsType = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly id: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly fields?: ReadonlyArray<any> | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly issueId: any;
	readonly childSummary?: ResponseJiraChildSummaryField | null;
};

export const addChildToParentListView = <NodeType extends BaseFieldsType>(
	parentIssue: NodeType,
): NodeType => ({
	...parentIssue,
	childSummary: {
		__typename: 'JiraWorkManagementChildSummary',
		hasChildren: true,
		totalCount: parentIssue.childSummary?.totalCount ? parentIssue.childSummary?.totalCount + 1 : 1,
	},
});

export const removeChildFromParentListView = <NodeType extends BaseFieldsType>(
	parentIssue: NodeType,
	childrenToRemove = 1,
): NodeType => {
	const newCount = parentIssue.childSummary?.totalCount
		? parentIssue.childSummary?.totalCount - childrenToRemove
		: 0;

	return {
		...parentIssue,
		childSummary: {
			__typename: 'JiraWorkManagementChildSummary',
			hasChildren: newCount > 0,
			totalCount: newCount,
		},
	};
};

const createDateTimeFieldTransformer = () => (field: ResponseJiraDateTimePickerField) => ({
	[field.aliasFieldId || field.fieldId]:
		(field.dateTime && DateOnly.fromDateAccordingToLocalTime(new Date(field.dateTime))) ?? null,
});

const createDateFieldTransformer = () => (field: ResponseJiraDatePickerField) => ({
	[field.aliasFieldId || field.fieldId]: (field.date && toDateOnly(field.date)) ?? null,
});

const atlassianAccountUserTransformer = (user: ResponseAtlassianUserAccount) => ({
	accountId: user.accountId,
	avatarUrl: user.picture,
	displayName: user.name,
});

const createJiraSingleSelectUserPickeTransformer =
	() => (field: ResponseJiraSingleSelectUserPickerField) =>
		(field.user && {
			[field.aliasFieldId || field.fieldId]: atlassianAccountUserTransformer(field.user),
		}) ||
		null;

const createJiraSingleLineTextTransformer =
	(fieldId?: string) => (field: ResponseJiraSingleLineTextField) => ({
		[fieldId || field.aliasFieldId || field.fieldId]: field.text,
	});

const createJiraLabelsTransformer = () => (field: ResponseJiraLabelsField) => ({
	[field.aliasFieldId || field.fieldId]:
		field.selectedLabelsConnection?.edges?.map((edge) => edge?.node?.name) || [],
});

const topLevelFieldTransformers = {
	[ISSUE_KEY_TYPE]: createJiraSingleLineTextTransformer('key'),
	[ISSUE_TYPE]: (field: ResponseJiraIssueTypeField) => ({
		issuetype: {
			iconUrl: field.issueType?.avatar?.medium,
			id: field.issueType?.issueTypeId,
			name: field.issueType?.name,
			level: field.issueType?.hierarchy?.level,
		},
	}),
	[SUMMARY_TYPE]: createJiraSingleLineTextTransformer(),
	[STATUS_TYPE]: (field: ResponseJiraStatusField) => ({
		status: {
			id: field.status.statusId,
			name: field.status.name,
			categoryId: field.status.statusCategory?.statusCategoryId,
		},
	}),
	[START_DATE_ALIAS_FIELD_ID]: createDateFieldTransformer(),
	[DUE_DATE_TYPE]: createDateFieldTransformer(),
	[UPDATED]: createDateTimeFieldTransformer(),
	[CREATED]: createDateTimeFieldTransformer(),
	[ASSIGNEE_TYPE]: createJiraSingleSelectUserPickeTransformer(),
	[PRIORITY_TYPE]: (field: ResponseJiraPriorityField) => ({
		priority:
			(field.priority && {
				id: field.priority.id,
				name: field.priority.name,
				iconUrl: field.priority.iconUrl,
			}) ??
			null,
	}),

	[LABELS_TYPE]: createJiraLabelsTransformer(),
	[RANK_CF_TYPE]: createJiraSingleLineTextTransformer(),
	[TIME_ESTIMATE_TYPE]: (field: ResponseJiraOriginalTimeEstimateField) => ({
		timeoriginalestimate: field.originalEstimate?.timeInSeconds ?? null,
	}),
};

const getTopLevelField = (field: ResponseJiraField) => {
	const transformer =
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		topLevelFieldTransformers[field.type as keyof typeof topLevelFieldTransformers];

	if (transformer) {
		// @ts-expect-error - TS2345 - Type &#39;JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraColorField&#39; is not assignable to type &#39;never&#39;
		return transformer(field);
	}

	return null;
};

export const transformResult = (
	edges: (ItemEdge | null)[],
	options: {
		cacheVariables: JWMGetItemsCacheVariables;
		intl: IntlShape;
	},
): ListViewIssue[] => {
	if (edges) {
		return edges
			.filter((edge) => {
				return Array.isArray(edge?.node?.fields);
			})
			.map((edge) => {
				const {
					id,
					fields,
					issueId,
					childSummary,
					legacyConfluenceLinks,
					commentSummary,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				} = edge!.node!;

				const listViewIssue: Partial<ListViewIssue> = {
					id,
					issueId,
					childSummary: {
						totalCount: childSummary?.totalCount,
						hasChildren: childSummary?.hasChildren ?? false,
					},
					cacheVariables: options.cacheVariables,
					fields: listViewTransformFields(fields, options.intl),
					confluenceLinks: legacyConfluenceLinks ?? undefined,
					commentSummary: {
						totalCount: commentSummary?.totalCount,
					},
					...transformTopLevelFields(edge?.node),
				};

				fields?.forEach((field) => {
					if (field != null) {
						const fieldValues = getTopLevelField(field);
						Object.assign(listViewIssue, fieldValues);
					}
				});

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				return listViewIssue as ListViewIssue;
			});
	}

	return [];
};
